import { useState, useMemo, useCallback } from 'react';
import {
  useDebtNotifications,
  useSendDebtNotifications,
  fetchDebtNotificationsBulk,
} from '../../../hooks/useDebt';
import {
  buildNotificationRowKey,
  aggregateSelectedNotifications,
  buildNotificationsPayload,
  hasNotificationEmails,
} from '../utils/notificationsUtils';

const formatDateInput = (date) => {
  if (!date) return '';
  const instance = date instanceof Date ? date : new Date(date);
  return Number.isNaN(instance.getTime()) ? '' : instance.toISOString().split('T')[0];
};

const INITIAL_FILTER = (() => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return {
    fromDate: formatDateInput(firstDay),
    toDate: formatDateInput(lastDay),
  };
})();

const NOTIFICATIONS_ITEMS_PER_PAGE = 10;

export const useDebtNotificationsTable = () => {
  const sendDebtNotificationsMutation = useSendDebtNotifications();

  const [filter, setFilter] = useState(INITIAL_FILTER);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(NOTIFICATIONS_ITEMS_PER_PAGE);
  const [selectedMap, setSelectedMap] = useState({});
  const [notificationResults, setNotificationResults] = useState(null);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  const queryFilters = useMemo(() => {
    const params = {
      page,
      per_page: rowsPerPage,
    };

    if (filter.fromDate) params.from_date = filter.fromDate;
    if (filter.toDate) params.to_date = filter.toDate;

    return params;
  }, [filter, page, rowsPerPage]);

  const {
    data: notificationsResponse,
    isLoading,
    isFetching,
  } = useDebtNotifications(queryFilters, { keepPreviousData: true });

  const results = notificationsResponse?.results || [];
  const summary = notificationsResponse?.summary || null;
  const totalRecords = notificationsResponse?.total_records ?? results.length;
  const totalPages = notificationsResponse?.pages ?? (totalRecords > 0 ? 1 : 0);
  const currentPage = notificationsResponse?.page ?? page;

  const selectedSummary = useMemo(
    () => aggregateSelectedNotifications(selectedMap),
    [selectedMap]
  );

  const handleDateChange = useCallback((event) => {
    const { name, value } = event.target || {};
    if (!name) return;

    setFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPage(1);
  }, []);

  const handleDatePreset = useCallback((preset) => {
    const today = new Date();
    let from = new Date(today);
    let to = new Date(today);

    if (preset === 'currentMonth') {
      from = new Date(today.getFullYear(), today.getMonth(), 1);
      to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (preset === 'next30') {
      to = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    }

    setFilter({
      fromDate: formatDateInput(from),
      toDate: formatDateInput(to),
    });
    setPage(1);
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilter(INITIAL_FILTER);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((event, value) => {
    setPage(value);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value)) {
      setRowsPerPage(value);
      setPage(1);
    }
  }, []);

  const toggleRowSelection = useCallback((header, coupons = [], forceSelect = null) => {
    if (!hasNotificationEmails(header)) {
      return;
    }

    const key = buildNotificationRowKey(header);
    setSelectedMap((prev) => {
      const currentEntry = prev[key] || { header, coupons: {} };
      const currentCoupons = currentEntry.coupons || {};
      const shouldSelectAll =
        forceSelect !== null
          ? forceSelect
          : Object.keys(currentCoupons).length !== coupons.length;

      const nextState = { ...prev };

      if (shouldSelectAll) {
        const nextCoupons = {};
        coupons.forEach((coupon) => {
          if (coupon?.uuid) {
            nextCoupons[coupon.uuid] = coupon;
          }
        });
        if (Object.keys(nextCoupons).length > 0) {
          nextState[key] = {
            header,
            coupons: nextCoupons,
          };
        }
      } else {
        delete nextState[key];
      }

      return nextState;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedMap({});
  }, []);

  const openResultsDialog = useCallback((resultsData) => {
    setNotificationResults(resultsData);
    setResultsDialogOpen(true);
  }, []);

  const closeResultsDialog = useCallback(() => {
    setResultsDialogOpen(false);
    setNotificationResults(null);
  }, []);

  const selectAllNotifications = useCallback(async () => {
    if (isSelectingAll) return;

    setIsSelectingAll(true);
    try {
      const resultsAccumulator = await fetchDebtNotificationsBulk(filter);

      const nextState = {};
      resultsAccumulator.forEach((item = {}) => {
        const header = item.header || {};
        const coupons = item.coupons || [];
        if (!coupons.length) return;
        if (!hasNotificationEmails(header)) return;

        const couponMap = {};
        coupons.forEach((coupon) => {
          if (coupon?.uuid) {
            couponMap[coupon.uuid] = coupon;
          }
        });

        if (Object.keys(couponMap).length > 0) {
          const key = buildNotificationRowKey(header);
          nextState[key] = {
            header,
            coupons: couponMap,
          };
        }
      });

      setSelectedMap(nextState);
    } catch (error) {
      console.error('Error selecting all notifications:', error);
    } finally {
      setIsSelectingAll(false);
    }
  }, [filter, isSelectingAll]);

  const isRowFullySelected = useCallback(
    (header) => {
      const key = buildNotificationRowKey(header);
      return Boolean(selectedMap[key]);
    },
    [selectedMap]
  );

  const buildSendPayload = useCallback(
    (message) => buildNotificationsPayload(selectedMap, message),
    [selectedMap]
  );

  return {
    filter,
    page: currentPage,
    totalPages,
    totalCount: totalRecords,
    rowsPerPage,
    data: results,
    summary,
    isLoading,
    isFetching,
    selectedMap,
    selectedSummary,
    notificationResults,
    resultsDialogOpen,
    openResultsDialog,
    closeResultsDialog,
    isSelectingAll,
    selectAllNotifications,
    handleDateChange,
    handleDatePreset,
    handleResetFilters,
    handlePageChange,
    handleRowsPerPageChange,
    toggleRowSelection,
    isRowFullySelected,
    clearSelection,
    buildSendPayload,
    sendDebtNotificationsMutation,
  };
};
